import {
  Building2, CalendarClock, CheckCheck, ClipboardList, Clock, DollarSign, Gift, Library, LogOut, Menu, Network, Package, Settings, UserCircle, Users, X,
} from "lucide-react";
import React, { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { api, logoUrl } from "../lib/api";
import { isManagerUser, useAuth } from "../lib/auth";
import { DEFAULT_NAME, useBranding, useLabels } from "../lib/queries";
import { cx } from "./wf";

function useMenu() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const mgr = isManagerUser(user);
  const labels = useLabels(isAdmin);
  const billing = useQuery({ queryKey: ["billing-status", user?.id], queryFn: () => api("/billing/status"), enabled: !!user });
  const dash = useQuery({ queryKey: ["dashboard", { mine: true }], queryFn: () => api("/dashboard?mine=true"), enabled: !!user });
  const projects = useQuery({ queryKey: ["projects"], queryFn: () => api("/projects"), enabled: !!user, staleTime: 60_000 });
  const approvals = (projects.data ?? []).reduce((n, p) => n + (p.awaiting_approval ?? 0), 0);
  return [
    { label: "Jobs", icon: ClipboardList, to: "/", end: true, badge: dash.data?.new_assigned },
    { label: "Completed Jobs", icon: CheckCheck, to: "/completed-jobs" },
    { label: "Customers", icon: Building2, to: "/customers" },
    { label: "Team", icon: Users, to: "/team" },
    { label: "Parts", icon: Package, to: "/parts" },
    isAdmin && { label: labels.ny_tab, icon: DollarSign, to: "/budget-jobs", money: true, id: "ny-jobs" },
    isAdmin && { label: "Hourly Jobs", icon: DollarSign, to: "/hourly-jobs", money: true },
    { label: "Projects", icon: Network, to: "/projects", badge: approvals },
    mgr && { label: "Team Hours", icon: Clock, to: "/team-hours" },
    { label: "Documentation", icon: Library, to: "/documentation" },
    mgr && { label: "Settings", icon: Settings, to: "/settings" },
    { label: "My Profile", icon: UserCircle, to: "/profile" },
    billing.data?.is_owner && { label: "Free Codes", icon: Gift, to: "/free-codes" },
  ].filter(Boolean);
}

function Brand() {
  const b = useBranding();
  return (
    <div className="flex items-center gap-3 px-3">
      <img
        src={b.data?.has_logo ? logoUrl(b.data) : "/forgeops-icon.png"}
        alt="logo"
        data-testid="sidebar-logo"
        className="h-10 w-10 rounded-xl border border-wf-line object-cover"
      />
      <div className="min-w-0">
        <p data-testid="sidebar-company-name" className="truncate text-[15px] font-extrabold leading-tight">{b.data?.company_name || DEFAULT_NAME}</p>
        <p className="flex items-center gap-1 text-xs text-wf-mute"><img src="/forgeops-192.png" alt="" className="h-3.5 w-3.5 rounded-[3px]" />ForgeOps</p>
      </div>
    </div>
  );
}

function NavItems({ onPick }) {
  const items = useMenu();
  return (
    <nav className="flex flex-col gap-0.5">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          onClick={onPick}
          data-testid={`nav-${it.id ?? it.label.replace(/\s+/g, "-").toLowerCase()}`}
          className={({ isActive }) =>
            cx(
              "flex min-h-[44px] items-center gap-3 rounded-xl px-3 text-[15px] font-semibold transition-colors",
              isActive ? "bg-wf-soft text-wf-brand" : "text-wf-ink2 hover:bg-wf-s3",
              it.money && "text-green-700",
            )
          }
        >
          <it.icon size={19} />
          <span className="flex-1 truncate">{it.label}</span>
          {!!it.badge && <span data-testid={`nav-${it.id ?? it.label.replace(/\s+/g, "-").toLowerCase()}-badge`} className="rounded-full bg-wf-err px-2 py-0.5 text-[11px] font-bold text-white">{it.badge}</span>}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarBody({ onPick }) {
  const { user, logout } = useAuth();
  return (
    <div className="flex h-full flex-col gap-6 py-6">
      <Brand />
      <div className="flex-1 overflow-y-auto px-3">
        <NavItems onPick={onPick} />
      </div>
      <div className="border-t border-wf-line px-3 pt-4">
        <p className="truncate px-3 text-sm font-bold" data-testid="sidebar-user-name">{user?.name}</p>
        <p className="mb-2 truncate px-3 text-xs text-wf-mute">{user?.email}</p>
        <button data-testid="logout-button" onClick={logout} className="flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 text-[15px] font-semibold text-wf-err hover:bg-red-50">
          <LogOut size={19} /> Log Out
        </button>
      </div>
    </div>
  );
}

function TrialBanner() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const s = useQuery({ queryKey: ["billing-status", user?.id], queryFn: () => api("/billing/status"), enabled: !!user }).data;
  if (!s || s.free_access) return null;
  const redeem = async () => {
    setBusy(true);
    try {
      await api("/billing/redeem", { method: "POST", body: { code: code.trim() } });
      qc.invalidateQueries({ queryKey: ["billing-status"] });
      toast.success("Code accepted — free access unlocked");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  if (!s.trial_active)
    return (
      <div data-testid="trial-ended-banner" className="flex flex-wrap items-center gap-3 bg-orange-50 px-5 py-2.5 text-sm font-semibold text-orange-900">
        <CalendarClock size={16} /> <span className="flex-1">Free trial ended · subscribe from the mobile app, or enter a free access code</span>
        <input data-testid="paywall-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Code"
          className="h-9 w-36 rounded-lg border border-orange-200 bg-white px-3 font-mono text-sm outline-none focus:border-wf-brand" />
        <button data-testid="paywall-redeem" disabled={!code.trim() || busy} onClick={redeem} className="h-9 rounded-lg bg-wf-brand px-4 font-bold text-wf-onbrand disabled:opacity-50">{busy ? "Checking…" : "Redeem"}</button>
      </div>
    );
  if (s.trial_active)
    return (
      <div data-testid="trial-banner" className="flex items-center gap-2 bg-wf-soft px-5 py-2 text-sm font-semibold text-wf-softink">
        <CalendarClock size={16} /> {s.trial_days_left} day{s.trial_days_left === 1 ? "" : "s"} left in your free trial · subscribe from the mobile app
      </div>
    );
  return null;
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex min-h-screen bg-wf-surface">
      <aside className="sticky top-0 hidden h-screen w-[272px] shrink-0 border-r border-wf-line bg-wf-s2 lg:block" data-testid="sidebar">
        <SidebarBody />
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 flex lg:hidden" data-testid="mobile-menu">
          <div className="w-[290px] bg-wf-surface shadow-2xl">
            <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="absolute left-[244px] top-5 rounded-full p-2 hover:bg-wf-s3">
              <X size={20} />
            </button>
            <SidebarBody onPick={() => setOpen(false)} />
          </div>
          <div className="flex-1 bg-black/35" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-3 border-b border-wf-line px-4 py-3 lg:hidden">
          <button data-testid="mobile-menu-button" onClick={() => setOpen(true)} className="rounded-full p-2 hover:bg-wf-s3">
            <Menu size={22} />
          </button>
          <Brand />
        </div>
        <TrialBanner />
        <div className="flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
